import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import Modal from '../ui/Modal';
import { moduleColor } from '../ui/modulePalette';
import { IconAlertTriangle, IconCheck, IconEdit, IconExternalLink, IconPlus, IconTrash } from '../ui/icons';

function formatDateTime(input) {
  const raw = String(input || '').trim();
  if (!raw) return '—';
  const d = new Date(raw.replace(' ', 'T'));
  if (!Number.isFinite(d.getTime())) return raw;
  try {
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'full', timeStyle: 'short' }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function normalizeLevel(raw) {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'major' || v === 'majeur') return 'major';
  return 'minor';
}

function splitChangesText(raw) {
  return String(raw || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((l) => l.trim().replace(/^[-•\t ]+/, '').trim())
    .filter(Boolean)
    .slice(0, 200);
}

function moduleIsAdmin(modules, moduleKey) {
  const key = String(moduleKey || '').trim();
  if (!key) return false;
  const list = Array.isArray(modules) ? modules : [];
  const found = list.find((m) => String(m?.key || '') === key) || null;
  return Boolean(found?.is_admin);
}

export default function UpdatesPage({ siteId, user, modules }) {
  const [state, setState] = useState({ status: 'loading', error: '', updates: [] });
  const [modal, setModal] = useState(null);

  const accent = moduleColor('updates');

  function notifyUpdatesNew() {
    try {
      window.dispatchEvent(
        new CustomEvent('omniscope:moduleEvent', {
          detail: {
            moduleKey: 'updates',
            siteId: Number(siteId || 0),
            type: 'created',
            at: Date.now(),
          },
        })
      );
    } catch {
      // ignore
    }
  }
  const canAdminOps = useMemo(() => {
    const isSuperadmin = Boolean(String(user?.role || '') === 'admin' && user?.is_superadmin);
    if (isSuperadmin) return true;
    return moduleIsAdmin(modules, 'updates');
  }, [user, modules]);

  function logMailDebug(label, mail) {
    const m = mail && typeof mail === 'object' ? mail : null;
    if (!m) return;
    try {
      const ok = Boolean(m.ok);
      const transport = String(m.transport || '');
      const msgId = m?.meta?.message_id ? String(m.meta.message_id) : '';
      const qid = m.smtp_queue_id ? String(m.smtp_queue_id) : '';
      const title = `[mail] ${label} — ${ok ? 'OK' : 'FAIL'}${transport ? ` (${transport})` : ''}${qid ? ` — ${qid}` : ''}${msgId ? ` — ${msgId}` : ''}`;
      (console.groupCollapsed || console.group).call(console, title);
      if (m.meta) console.log('meta', m.meta);
      if (qid) console.log('smtp_queue_id', qid);
      if (m.smtp_response) console.log('smtp_response', m.smtp_response);
      if (Array.isArray(m.trace)) console.table(m.trace);
      if (!ok && m.error) console.warn('error', m.error);
      console.groupEnd();
    } catch {
      // ignore
    }
  }

  const refresh = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .updatesList(Number(siteId), { limit: 250 })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', error: res.error || 'Impossible de charger', updates: [] });
          return;
        }
        setState({ status: 'ready', error: '', updates: res.updates || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', error: 'Impossible de charger', updates: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  useEffect(() => refresh(), [refresh]);

  const updates = useMemo(() => (Array.isArray(state.updates) ? state.updates : []), [state.updates]);

  function openNewUpdate() {
    setModal({
      type: 'new',
      saving: false,
      error: '',
      title: '',
      level: 'minor',
      changes_text: '',
      send_email: false,
      email_to: '',
      mail_result: null,
    });
  }

  function openEditUpdate(update) {
    const u = update || {};
    const changesText = Array.isArray(u.changes) ? u.changes.join('\n') : '';
    setModal({
      type: 'edit',
      saving: false,
      error: '',
      id: Number(u.id),
      title: String(u.title || ''),
      level: normalizeLevel(u.level),
      changes_text: changesText,
    });
  }

  async function confirmCreateUpdate() {
    if (!modal || modal.type !== 'new') return;
    if (modal.saving) return;
    const level = normalizeLevel(modal.level);
    const changes = splitChangesText(modal.changes_text);
    const sendEmail = Boolean(modal.send_email) || level === 'major';
    setModal((m) => (m ? { ...m, saving: true, error: '', mail_result: null } : m));
    const res = await api.updatesCreate({
      site_id: Number(siteId),
      title: modal.title,
      level,
      changes,
      send_email: sendEmail,
      email_to: modal.email_to,
    });
    if (!res.ok) {
      setModal((m) => (m ? { ...m, saving: false, error: res.error || 'Erreur' } : m));
      return;
    }
    if (res.mail) logMailDebug('Mise à jour — envoi', res.mail);
    setState((s) => ({ ...(s || {}), updates: res.update ? [res.update, ...(s.updates || [])] : s.updates }));
    notifyUpdatesNew();
    if (res.mail && !res.mail.ok) {
      setModal((m) => (m ? { ...m, saving: false, mail_result: res.mail } : m));
      return;
    }
    setModal(null);
  }

  async function confirmEditUpdate() {
    if (!modal || modal.type !== 'edit') return;
    if (modal.saving) return;
    const level = normalizeLevel(modal.level);
    const changes = splitChangesText(modal.changes_text);
    setModal((m) => (m ? { ...m, saving: true, error: '' } : m));
    const res = await api.updatesUpdate({
      site_id: Number(siteId),
      id: Number(modal.id),
      title: modal.title,
      level,
      changes,
    });
    if (!res.ok) {
      setModal((m) => (m ? { ...m, saving: false, error: res.error || 'Erreur' } : m));
      return;
    }
    if (res.update) {
      setState((s) => ({
        ...(s || {}),
        updates: (s.updates || []).map((u) => (Number(u?.id) === Number(res.update.id) ? res.update : u)),
      }));
    }
    setModal(null);
  }

  async function confirmDeleteUpdate() {
    if (!modal || modal.type !== 'delete') return;
    if (modal.saving) return;
    const id = Number(modal.id || 0);
    if (!Number.isFinite(id) || id <= 0) return;
    setModal((m) => (m ? { ...m, saving: true, error: '' } : m));
    const res = await api.updatesDelete({ site_id: Number(siteId), id });
    if (!res.ok) {
      setModal((m) => (m ? { ...m, saving: false, error: res.error || 'Erreur' } : m));
      return;
    }
    setState((s) => ({ ...(s || {}), updates: (s.updates || []).filter((u) => Number(u?.id) !== id) }));
    setModal(null);
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Mises à jour' }]} />
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <div className="Card" style={{ marginBottom: 14 }}>
        <div className="Row" style={{ marginBottom: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div className="TitleMd">Historique</div>
          </div>
          <div className="Badges">
            {canAdminOps ? (
              <button type="button" className="Button" onClick={openNewUpdate}>
                <span className="ButtonInline">
                  <IconPlus /> Nouvelle mise à jour
                </span>
              </button>
            ) : null}
          </div>
        </div>

        {state.status === 'loading' ? <div className="Muted">Chargement…</div> : null}

        {state.status !== 'loading' && updates.length === 0 ? <div className="Muted">Aucune mise à jour.</div> : null}

        <div className="List" role="list" style={{ marginTop: 8 }}>
          {updates.map((u) => {
            const level = normalizeLevel(u.level);
            const isMajor = level === 'major';
            const when = formatDateTime(u.created_at);
            const mailWanted = Boolean(u.send_email);
            const mailSent = Boolean(u.email_sent_at);
            const mailError = String(u.email_last_error || '').trim();
            const mailTo = String(u.email_to || '').trim();
            const changes = Array.isArray(u.changes) ? u.changes : [];
            const showChanges = changes.slice(0, 10);
            const hasMore = changes.length > 10;
            return (
              <div key={u.id} className="ListItem UpdatesItem" role="listitem">
                <div className="UpdatesItemMain" style={{ minWidth: 0 }}>
                  <div className="ButtonInline" style={{ justifyContent: 'space-between', width: '100%' }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="OneLine" style={{ fontWeight: 750 }}>
                        {u.title || 'Mise à jour'}
                      </div>
                      <div className="Muted" style={{ marginTop: 6 }}>
                        {when}
                      </div>
                    </div>
                    <div className="Badges" style={{ justifyContent: 'flex-end' }}>
                      <span
                        className="Badge"
                        style={{
                          borderColor: isMajor ? '#ef4444' : accent,
                          color: isMajor ? '#ef4444' : accent,
                          background: `color-mix(in srgb, ${isMajor ? '#ef4444' : accent} 16%, transparent)`,
                        }}
                        title={isMajor ? 'Mise à jour majeure' : 'Mise à jour mineure'}
                      >
                        {isMajor ? 'majeur' : 'mineur'}
                      </span>
                      {mailWanted ? (
                        <span className="Badge" title={mailSent ? `Email envoyé${mailTo ? ` à ${mailTo}` : ''}` : mailError || 'Email non envoyé'}>
                          <span className="ButtonInline" style={{ gap: 8 }}>
                            {mailSent ? <IconCheck /> : <IconAlertTriangle />}
                            <span>{mailSent ? 'mail envoyé' : 'mail'}</span>
                          </span>
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {showChanges.length ? (
                    <ul style={{ margin: '10px 0 0 0', paddingLeft: 18 }}>
                      {showChanges.map((c, idx) => (
                        <li key={`${u.id}_c_${idx}`} style={{ margin: '0 0 6px 0' }}>
                          {c}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {!showChanges.length ? <div className="Muted" style={{ marginTop: 10 }}>—</div> : null}

                  {hasMore ? (
                    <div style={{ marginTop: 10 }}>
                      <button type="button" className="Button Button--ghost" onClick={() => setModal({ type: 'view', update: u })}>
                        Voir plus…
                      </button>
                    </div>
                  ) : null}

                  {mailWanted && !mailSent && mailError ? (
                    <div className="Error" role="alert" style={{ marginTop: 10 }}>
                      {mailError}
                    </div>
                  ) : null}
                </div>

                <div className="Badges UpdatesItemActions" style={{ justifyContent: 'flex-end' }}>
                  {canAdminOps ? (
                    <>
                      <button
                        type="button"
                        className="Button Button--ghost Button--icon"
                        onClick={() => openEditUpdate(u)}
                        title="Modifier (sans email)"
                        aria-label="Modifier"
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className="Button Button--ghost Button--danger Button--icon"
                        onClick={() => setModal({ type: 'delete', id: Number(u.id), title: String(u.title || ''), saving: false, error: '' })}
                        title="Supprimer"
                        aria-label="Supprimer"
                      >
                        <IconTrash />
                      </button>
                    </>
                  ) : null}
                  {mailWanted && mailTo ? (
                    <a className="Button Button--ghost Button--icon" href={`mailto:${mailTo}`} title="Écrire un email">
                      <IconExternalLink />
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal?.type === 'view' ? (
        <Modal
          title={modal.update?.title || 'Mise à jour'}
          icon={<IconCheck />}
          onClose={() => setModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
              Fermer
            </button>
          }
        >
          <div className="Muted" style={{ marginBottom: 12 }}>
            {formatDateTime(modal.update?.created_at)}
          </div>
          <div className="List" role="list">
            {(Array.isArray(modal.update?.changes) ? modal.update.changes : []).map((c, idx) => (
              <div key={`full_${idx}`} className="ListItem" role="listitem">
                {c}
              </div>
            ))}
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'new' ? (
        <Modal
          title="Nouvelle mise à jour"
          icon={<IconPlus />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button" onClick={confirmCreateUpdate} disabled={modal.saving}>
                {modal.saving ? 'Enregistrement…' : modal.level === 'major' ? 'Créer et envoyer' : 'Créer'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Titre</label>
            <input
              className="Input"
              value={modal.title}
              onChange={(e) => setModal((m) => (m ? { ...m, title: e.target.value } : m))}
              disabled={modal.saving}
              placeholder="Ex: Refonte du module Ressources"
            />
          </div>

          <div className="FormRow3" style={{ marginTop: 10 }}>
            <div className="Field">
              <label className="Label">Niveau</label>
              <div className="Seg" role="group" aria-label="Niveau">
                <button
                  type="button"
                  className={`SegButton ${normalizeLevel(modal.level) === 'minor' ? 'is-active' : ''}`}
                  onClick={() => setModal((m) => (m ? { ...m, level: 'minor' } : m))}
                  disabled={modal.saving}
                  title="Mise à jour mineure"
                >
                  mineur
                </button>
                <button
                  type="button"
                  className={`SegButton ${normalizeLevel(modal.level) === 'major' ? 'is-active' : ''}`}
                  onClick={() => setModal((m) => (m ? { ...m, level: 'major', send_email: true } : m))}
                  disabled={modal.saving}
                  title="Mise à jour majeure (email obligatoire)"
                >
                  majeur
                </button>
              </div>
            </div>
            <div className="Field">
              <label className="Label">Email client</label>
              <input
                className="Input"
                value={modal.email_to}
                onChange={(e) => setModal((m) => (m ? { ...m, email_to: e.target.value } : m))}
                disabled={modal.saving || (normalizeLevel(modal.level) !== 'major' && !modal.send_email)}
                placeholder={normalizeLevel(modal.level) === 'major' || modal.send_email ? 'Optionnel (par défaut: email du client)' : 'Active “envoyer un mail”'}
              />
            </div>
            <div className="Field">
              <label className="Label">Envoyer un mail</label>
              <label className="ButtonInline" style={{ gap: 10, justifyContent: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={normalizeLevel(modal.level) === 'major' ? true : Boolean(modal.send_email)}
                  onChange={(e) => setModal((m) => (m ? { ...m, send_email: Boolean(e.target.checked) } : m))}
                  disabled={modal.saving || normalizeLevel(modal.level) === 'major'}
                />
                <span className="Muted">{normalizeLevel(modal.level) === 'major' ? 'Obligatoire' : 'Optionnel'}</span>
              </label>
            </div>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Liste des modifications (1 par ligne)</label>
            <textarea
              className="Textarea"
              rows={8}
              value={modal.changes_text}
              onChange={(e) => setModal((m) => (m ? { ...m, changes_text: e.target.value } : m))}
              disabled={modal.saving}
              placeholder={'Ex:\n- Correction du menu mobile\n- Optimisation du chargement des images\n- Amélioration du SEO'}
            />
          </div>

          {modal.mail_result && modal.mail_result.ok ? (
            <div className="Notice" style={{ marginTop: 12 }}>
              <span className="ButtonInline">
                <IconCheck /> Email envoyé.
              </span>
            </div>
          ) : null}
          {modal.mail_result && !modal.mail_result.ok ? (
            <div className="Error" role="alert" style={{ marginTop: 12 }}>
              {modal.mail_result.error || 'Email non envoyé'}
            </div>
          ) : null}
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 12 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'edit' ? (
        <Modal
          title="Modifier la mise à jour"
          icon={<IconEdit />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button" onClick={confirmEditUpdate} disabled={modal.saving}>
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="Notice">
            <span className="ButtonInline">
              <IconCheck /> Aucun email ne sera envoyé lors de la modification.
            </span>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Titre</label>
            <input
              className="Input"
              value={modal.title}
              onChange={(e) => setModal((m) => (m ? { ...m, title: e.target.value } : m))}
              disabled={modal.saving}
            />
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Niveau</label>
            <div className="Seg" role="group" aria-label="Niveau">
              <button
                type="button"
                className={`SegButton ${normalizeLevel(modal.level) === 'minor' ? 'is-active' : ''}`}
                onClick={() => setModal((m) => (m ? { ...m, level: 'minor' } : m))}
                disabled={modal.saving}
              >
                mineur
              </button>
              <button
                type="button"
                className={`SegButton ${normalizeLevel(modal.level) === 'major' ? 'is-active' : ''}`}
                onClick={() => setModal((m) => (m ? { ...m, level: 'major' } : m))}
                disabled={modal.saving}
              >
                majeur
              </button>
            </div>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Description / liste des modifications (1 par ligne)</label>
            <textarea
              className="Textarea"
              rows={10}
              value={modal.changes_text}
              onChange={(e) => setModal((m) => (m ? { ...m, changes_text: e.target.value } : m))}
              disabled={modal.saving}
            />
          </div>

          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 12 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'delete' ? (
        <Modal
          title="Supprimer la mise à jour"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button Button--danger" onClick={confirmDeleteUpdate} disabled={modal.saving}>
                {modal.saving ? 'Suppression…' : 'Supprimer'}
              </button>
            </>
          }
        >
          <div className="Muted">
            Supprimer définitivement <strong>{modal.title || 'cette mise à jour'}</strong> de l’historique ?
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
